import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { UserStore } from '@/utils/UserStore';

function hasRole(requiredRole: string): Boolean {
  const roles = ['ROLE_USER', 'ROLE_AGENCE', 'ROLE_VISUALISATEUR', 'ROLE_COLLECTEUR', 'ROLE_ENTREPRISE', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];
  const user = UserStore();
  //const userRole = user.getRole;
  const userRole = user.getRoleAPI;

  return roles.indexOf(userRole) >= roles.indexOf(requiredRole);
}

function isAuthenticated() {
  const user = UserStore();
  return user.isConnect
}

const ifAuthenticated = async (to: any, from: any, next: any) => {
  if (isAuthenticated()) next();
  else next({ name: 'login' });
}

const ifAuthorized = (role: string) => async (to: any, from: any, next: any) => {
  if (!isAuthenticated()) {
    next({ name: 'login' }); // Redirige vers la page de login si non connecté
    return;
  }

  if (hasRole(role)) {
    next(); // L'utilisateur a le rôle requis
  } else {
    next('/unauthorized'); // Ou n'importe quelle route d'erreur
  }
};


function authorize(requiredRoles: string[]): boolean {
  return requiredRoles.some(role => hasRole(role));
}

const router = createRouter({
  //Cette ligne indique qu'on utilise la gestion html5 des urls
  //l'argument donné à la fonction createWebHistory sert de base pour la réécriture des routes
  //on utilise donc import.meta.env.BASE_URL qui correspond à la valeur donné à base dans le fichier vite.config.ts
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'home' }
    },
    {
      path: '',
      component: () => import('@/layouts/main/Main.vue'),
      beforeEnter: ifAuthenticated,
      children: [
        {
          path: '/dashboard',
          name: 'home',
          component: () => import('@/views/homePage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Tableau de bord",
          }
        },
        {
          path: '/projects',
          name: 'projects',
          component: () => import('@/views/administration/projets/index.vue'),
          meta: {
            pageTitle: "Liste des projets",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        {
          path: '/plan',
          name: 'abonnements',
          component: () => import('@/views/administration/plan/index.vue'),
          meta: {
            breadcrumb: [
              { title: "Gestion des Abonnements" },
            ],
            pageTitle: "Gestion des Abonnements",
            requiresAuth: true, roles: ['ROLE_SUPER_ADMIN']
          }
        },
        /* {
          path: '/project-settings',
          name: 'project-settings',
          component: () => import('@/views/project/indexProject.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Paramètres des projets",
          }
        }, */
        {
          path: '/project-settings',
          name: 'project-settings',
          component: () => import('@/views/project/settings/index.vue'),
          meta: {
            pageTitle: "Paramètres des projets",
            requiresAuth: true, roles: ['ROLE_VISUALISATEUR']
          }
        },
        {
          path: '/project-users',
          name: 'project-users',
          component: () => import('@/views/project/users/index.vue'),
          meta: {
            pageTitle: "Utilisateurs des projets",
            requiresAuth: true, roles: ['ROLE_VISUALISATEUR']
          }
        },
        /* {
          path: '/georeferencing-existing-networks',
          name: 'georeferencing',
          component: () => import('@/views/georeferencing/Georeferencing.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Géoréférencement des réseaux existants ",
          }
        }, */
        {
          path: '/georeferencing-existing-networks/:networkName?',
          name: 'georeferencing',
          component: () => import('@/views/georeferencing/Georeferencing.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: (route: RouteLocationNormalized): string => {
              const name = route.params.networkName
              return name
                //? `Géoréférencement du réseau ${name}`
                ? `Liste des points de charge l'enquête :  ${name}`
                : "Géoréférencement des réseaux existants"
            }
          }
        },
        {
          path: '/georeferencing-layer',
          name: 'layer',
          component: () => import('@/views/layer/LayerPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des réseaux existants ",
          }
        },
        {
          path: '/charging-points/list',
          name: 'charging-points',
          component: () => import('@/views/charging-points/ChargingPoints.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Liste des points de charge",
          }
        },
        {
          path: '/charging-points/map',
          name: 'Points de charge',
          component: () => import('@/views/map/CartePointsDeCharge.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Visualisation des points de charge",
          }
        },
        {
          path: '/demand-forecast/load-curve',
          name: 'Courbe de charge',
          component: () => import('@/views/demand-forecast/LoadCurve.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            // pageTitle: "Courbe de charge",
            pageTitle: "Profil utilisateurs finaux",
          }
        },
        /* {
          path: '/demand-forecast/params',
          name: 'Parametrage',
          component: () => import('@/views/demand-forecast/ParametrageView.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
              // pageTitle: "Courbe de charge",
              pageTitle: "Paramétrages",
          }
        }, */
        {
          path: '/map',
          name: 'map',
          component: () => import('@/views/map/indexMap.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Carte",
          }
        },
        {
          path: '/forms',
          name: 'form',
          component: () => import('@/views/forms/index.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Questionnaires",
          }
        },
        {
          path: '/forms/update/:id',
          name: 'form_update',
          component: () => import('@/views/forms/update.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Modification d'un questionnaire",
          }
        },
        {
          path: '/forms/detail/:id',
          name: 'form_detail',
          component: () => import('@/views/forms/detail.vue'),
          meta: {
            pageTitle: "Visualisation d'un questionnaire",
            requiresAuth: true, roles: ['ROLE_VISUALISATEUR']
          }
        },
        {
          path: '/forms/:id/enquetes',
          name: 'form_enquetes',
          component: () => import('@/views/forms/enquetes/index.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Enquetes du questionnaire",
          }
        },
        {
          path: '/forms/:id/enquetes/map',
          name: 'form_enquetes_map',
          component: () => import('@/views/forms/enquetes/map/index.vue'),
          meta: {
            pageTitle: "Carte des enquetes du questionnaire",
            requiresAuth: true, roles: ['ROLE_VISUALISATEUR']
          }
        },
        /* {
          path: '/seuil',
          name: 'Seuil',
          component: () => import('@/views/seuil/seuilPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
              pageTitle: "Gestion des seuils de population",
          }
        },
        {
          path: '/household-threshold',
          name: 'household_threshold',
          component: () => import('@/views/seuil/HouseholdThresholdPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
              pageTitle: "Gestion des seuils de population",
          }
        }, */
        {
          path: '/seuil',
          name: 'Seuil',
          component: () => import('@/views/seuil/seuilsPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des seuils",
          }
        },
        {
          path: '/consumer-type',
          name: 'consumer-type',
          // component: () => import('@/views/consumer-type/ConsumerTypePage.vue'),
          component: () => import('@/views/consumer-type/ConsumerTypesPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des types de consommateurs",
          }
        },
        {
          path: '/equipment',
          name: 'equipment',
          // component: () => import('@/views/equipment/EquipmentPage.vue'),
          component: () => import('@/views/equipment/EquipmentsPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des équipements",
          }
        },
        {
          path: '/services',
          name: 'services',
          // component: () => import('@/views/equipment/EquipmentPage.vue'),
          component: () => import('@/views/services/ServicesPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des services",
          }
        },
        {
          path: '/study-area',
          name: 'studyArea',
          component: () => import('@/views/studyArea/StudyAreaPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des zones d'étude",
          }
        },
        {
          path: '/projection',
          name: 'projection',
          component: () => import('@/views/projection/ProjectionPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des projections",
          }
        },
        {
          path: '/external-database-connection',
          name: 'external_database_connection',
          component: () => import('@/views/external-database-connection/ExternalDatabaseConnectionPage.vue'),
          beforeEnter: ifAuthorized('ROLE_USER'),
          meta: {
            pageTitle: "Gestion des connexions vers GISELEC",
          }
        },
        {
          path: '/users',
          name: 'users',
          component: () => import('@/views/administration/users/index.vue'),
          meta: {
            pageTitle: "Liste des Utilisateurs",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        {
          path: '/customers',
          name: 'customers',
          component: () => import('@/views/customers/index.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            pageTitle: "Clients",
          }
        },
        {
          path: '/doubles/customers',
          name: 'customersDoubles',
          component: () => import('@/views/customers/doubles/index.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            pageTitle: "Clients Doubles",
          }
        },
        {
          path: '/doubles/customers/:phone',
          name: 'customer_doubles_details',
          component: () => import('@/views/customers/doubles/details.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Clients" },
              { title: "Liste des clients doubles", url: '/doubles/customers', active: true },
              { title: "Détails du client", url: null, active: false },
            ],
            pageTitle: "Détails clients",
          }
        },
        {
          path: '/customers/:id',
          name: 'customer_details',
          component: () => import('@/views/customers/details.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Clients" },
              { title: "Liste des clients", url: '/customers', active: true },
              { title: "Détails", url: '/customers', active: true },
            ],
            pageTitle: "Détails du client",
          }
        },
        {
          path: '/places/:id/compters',
          name: 'place_compters',
          component: () => import('@/views/customers/place_counters.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Clients" },
              { title: "Liste des clients", url: '/customers', active: true },
              { title: "Places compteur du client", url: '/customers', active: true },
              { title: "Liste des compteurs", url: '', active: true },
            ],
            pageTitle: "Liste des compteurs",
          }
        },
        {
          path: '/reporting',
          name: 'reporting',
          component: () => import('@/views/reporting/index.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            pageTitle: "Reporting",
          }
        },
        /* {
          path: '/tables',
          name: 'tables',
          component: () => import('@/views/database/index.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            pageTitle: "Base de donnée",
          }
        } */
        {
          path: '/tables',
          name: 'tables',
          component: () => import('@/views/administration/database/index.vue'),
          meta: {
            pageTitle: "Base de donnée",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        /* {
          path: '/tables/customers/infos',
          name: 'table_customers_data',
          component: () => import('@/views/database/show/customer_table.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_Client",
          }
        },
        {
          path: '/tables/villages/infos',
          name: 'table_villages_data',
          component: () => import('@/views/database/show/village_table.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_VillageQuartier",
          }
        }, */
        {
          path: '/tables/customers/infos',
          name: 'table_customers_data',
          component: () => import('@/views/administration/database/show/customer_table.vue'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_Client",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        {
          path: '/tables/villages/infos',
          name: 'table_villages_data',
          component: () => import('@/views/administration/database/show/village_table.vue'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_VillageQuartier",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        /* {
          path: '/tables/places/infos',
          name: 'table_place_data',
          component: () => import('@/views/database/show/placeCompteur_table.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_PlaceCompteur",
          }
        },
        {
          path: '/tables/compteurs/infos',
          name: 'table_compteur_data',
          component: () => import('@/views/database/show/compter_table.vue'),
          beforeEnter: ifAuthorized('ROLE_ADMIN'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_Compteur",
          }
        }, */
        {
          path: '/tables/places/infos',
          name: 'table_place_data',
          component: () => import('@/views/administration/database/show/placeCompteur_table.vue'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_PlaceCompteur",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        {
          path: '/tables/compteurs/infos',
          name: 'table_compteur_data',
          component: () => import('@/views/administration/database/show/compter_table.vue'),
          meta: {
            breadcrumb: [
              { title: "Accès base de données" },
              { title: "Accès base de données", url: '/tables', active: true },
            ],
            pageTitle: "Consulter la table T_Compteur",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
        {
          path: '/logs',
          name: 'logs',
          component: () => import('@/views/administration/siteLogs/index.vue'),
          meta: {
            pageTitle: "Logs",
            requiresAuth: true, roles: ['ROLE_ADMIN']
          }
        },
      ]
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/Login.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/views/unauthorizedPage.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/auth/UserRegister.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/chooseProject',
      name: 'chooseProject',
      beforeEnter: ifAuthenticated,
      component: () => import('@/views/auth/ProjectChooseLogin.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/forgotPassword',
      name: 'forgotPassword',
      component: () => import('@/views/auth/ForgotPassword.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/reset-password/user/:id/token/:token',
      name: 'resetPassword',
      component: () => import('@/views/auth/ResetPasswordForm.vue'),
      meta: { requiresAuth: false }
    }
  ]


})


//router.beforeEach((to, from, next) => {
//  console.log(`Navigation de ${from.fullPath} vers ${to.fullPath}`);
//  isAuthenticated();
//  next();
//});

router.beforeEach((to, from, next) => {
  const user = UserStore();
  const isAuthenticated = user.isConnect;
  //console.log("isAuthenticated ",isAuthenticated);
  //console.log("requiresAuth ",to.meta.requiresAuth);
  if (to.meta.requiresAuth && !isAuthenticated) {
    //console.log(" next({ name: 'login' })");
    return next({ name: 'login' });
  }

  const roleMeta = to.meta.roles as string[] | undefined;

  // ⚠️ On vérifie SEULEMENT s’il y a des rôles définis
  if (roleMeta && roleMeta.length > 0) {

    const allowed = authorize(roleMeta);

    //console.log(`authorize(${roleMeta}) -> ${allowed}`);

    if (!allowed) {
      // Évite la boucle infinie
      if (to.name !== 'unauthorized') {
        return next({ name: 'unauthorized' });
      }
      return next(false);
    }
  }

  if ((to.name === 'login' || to.name === 'register') && isAuthenticated) {
    //console.log("next({ name: 'home' })");
    return next({ name: 'home' });
  }

  return next();
});

export default router
